import classNames from 'classnames';
import styles from './DIDQuery.module.css';
import { Button, Space, Form, Modal } from 'antd';
import { useState, useRef, useEffect } from 'react';
import SessionDefinition from 'src/components/Query/SessionDefinition';
import VehicleDefinition from '../../components/Query/VehicleDefinition';
import DIDDefinition from 'src/components/Query/DIDDefinition';
import ModuleDefitnition from 'src/components/Query/ModuleDefinition';
import FinalSummary from 'src/components/Query/FinalSummary';
import { getFinalResultCost } from 'src/app/query.service';

interface FormData {
  [key: string]: any;
}
interface Cost {
  queryCost: string;
  isCostThresholdCrossed: boolean;
}
interface ResettableComponent {
  reset: () => void;
}

const DIDQuery = () => {
  const [activeComponent, setActiveComponent] = useState<number>(1);
  const [formData, setFormData] = useState<FormData>({});
  const [form] = Form.useForm();
  const [finalStep, setFinalStep] = useState(false);
  const [costData, setCostData] = useState<Cost[]>([]);
  const [includeDidPackets, setCheckbox] = useState(false);
  const [isModalOpen, setModal] = useState(false);
  const sessionRef = useRef<ResettableComponent>(null);
  const vehicleRef = useRef<ResettableComponent>(null);
  const didRef = useRef<ResettableComponent>(null);
  const moduleRef = useRef<ResettableComponent>(null);
  const handleNext = (newData: FormData) => {
    setFormData((prevData) => ({ ...prevData, ...newData }));
    setActiveComponent((prevActiveComponent) => prevActiveComponent + 1);
  };

  const moduleDefData = (newData: FormData) => {
    setFormData((prevData) => ({ ...prevData, ...newData }));
  };
  const handleCheckbox = (decodeData: boolean) => {
    setCheckbox(decodeData);
  };
  const [payload, setPayload] = useState<FormData>({});

  const generatePayload = () => {
    const data = {
      didId: formData.didId || null,
      module: formData.module || null,
      includeDidPackets,
      hardwarePartNumber: formData.hwPartNumber || null,
      softwarePartNumber: formData.swPartNumber || null,
      ecuCalibrationNumber: formData.ecuCalibrationNumber || null,
      ecuSerialNumber: formData.ecuSerialNumber || null,
      dateFrom: formData.dateFrom || null,
      dateTo: formData.dateTo || null,
      sourceSoftwareVersion: formData.sourceSoftwareVersion || null,
      sourceType: formData.sourceType || null,
      bodyStyle: formData.bodyStyle || null,
      brand: formData.brand || null,
      driver: formData.driver || null,
      emission: formData.emission || null,
      engine: formData.engine || null,
      enginePowertrainDescription: formData.enginePowertrainDescription || null,
      market: formData.market || null,
      model: formData.model || null,
      modelYear: formData.modelYear || null,
      plant: formData.plant || null,
      trim: formData.trim || null,
      transmission: formData.transmission || null,
      vin: formData.vin || null,
      dateRangeOverriden: formData.dateRangeOverriden || null,
    };
    setPayload(data);
    return data;
  };

  const handleStart = async () => {
    const postData: FormData = generatePayload();

    try {
      const vinArray = Array.isArray(postData.vin) ? postData.vin : [];

      // If VINs are provided, skip cost check
      if (vinArray.length > 0) {
        setFinalStep(true);
        return;
      }

      const result: Cost[] = await getFinalResultCost<Cost>('did', 'final/cost', postData);
      setCostData(result);

      if (result[0]?.isCostThresholdCrossed) {
        setModal(true);
      } else {
        setFinalStep(true);
      }
    } catch (error) {
      console.log('error fetching cost...', error);
    }
  };

  useEffect(() => {
    if (costData[0]?.isCostThresholdCrossed) {
      setModal(true);
    }
  }, [costData]);

  const handleCancel = () => {
    setModal(false);
  };
  const handleStartQuery = () => {
    setModal(false);
    setFinalStep(true);
  };

  const handleReset = () => {
    form.resetFields();
    setFormData({});
    setActiveComponent(1);
    if (sessionRef.current) {
      sessionRef.current.reset();
    }
    if (vehicleRef.current) {
      vehicleRef.current.reset();
    }
    if (didRef.current) {
      didRef.current.reset();
    }
    if (moduleRef.current) {
      moduleRef.current.reset();
    }
  };
  const handleBack = () => {
    setActiveComponent((prevActiveComponent) => prevActiveComponent - 1);
  };
  return (
    <div className={classNames(styles.DIDQuery)}>
      {!finalStep && (
        <>
          <SessionDefinition
            ref={sessionRef}
            isActive={activeComponent === 1}
            onNext={handleNext}
            form={form}
            queryType="did"
          />
          <VehicleDefinition
            ref={vehicleRef}
            isActive={activeComponent === 2}
            onBack={handleBack}
            onNext={handleNext}
            propData={formData}
            form={form}
            queryType="did"
          />
          <DIDDefinition
            ref={didRef}
            isActive={activeComponent === 3}
            onBack={handleBack}
            onNext={handleNext}
            handleCheckbox={handleCheckbox}
            checkboxValue={includeDidPackets}
            propData={formData}
            form={form}
            queryType="did"
          />
          <ModuleDefitnition
            ref={moduleRef}
            isActive={activeComponent === 4}
            onBack={handleBack}
            propData={formData}
            form={form}
            queryType="did"
            handleNext={moduleDefData}
          />
          <div>
            <Space>
              <Button onClick={handleStart} className={styles['DIDQuery--Buttons']}>
                Start
              </Button>
              <Button className={styles['DIDQuery--Buttons']} onClick={handleReset}>
                Reset
              </Button>
            </Space>
          </div>
        </>
      )}
      {finalStep && (
        <FinalSummary
          onBack={() => setFinalStep(false)}
          queryType="did"
          propData={payload}
          filename="Did_export"
        />
      )}
      <Modal
        title={<h3 style={{ display: 'inline' }}>Query Cost Calculation</h3>}
        open={isModalOpen}
        onCancel={handleCancel}
        footer={null}
      >
        <div>
          Please be advised, this query may cost the company up to $ {costData[0]?.queryCost}. You
          can press &quto;Back&quto; to reduce this Cost by using a smaller date range and/or using
          specific VINs in you query. Please ensure this is business critical before selecting
          Start.
        </div>
        <div style={{ width: '100%' }}>
          <Button
            onClick={() => {
              setModal(false);
              setFinalStep(true);
            }}
            className="DidQuery--Buttons"
          >
            Start Query
          </Button>
          <Button
            onClick={() => setModal(false)}
            className="DidQuery--Buttons"
            style={{ float: 'right' }}
          >
            Back
          </Button>
        </div>
      </Modal>
    </div>
  );
};
export default DIDQuery;
